import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiErr } from "../../lib/api";
import { useLang } from "../../i18n";
import { PhaseIntro, AiButton, ReclusterButton, RevisitCallout } from "../../components/phase-common";
import DragBoard from "../../components/DragBoard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ChevronLeft, ChevronRight, Plus, X, Check, Sparkles, MessageCircle } from "lucide-react";

export default function Phase3({ org, refresh, isAdmin }) {
  const { t, lang } = useLang();
  const touchpoints = org.touchpoints || [];
  const feelings = org.feelings || [];

  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [pending, setPending] = useState(null); // text awaiting type choice
  const [ai, setAi] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [reBusy, setReBusy] = useState(false);

  const remaining = 3 - (org.ai_counts?.feelings || 0);
  const current = touchpoints[Math.min(idx, touchpoints.length - 1)];

  const linkedHere = useMemo(
    () => feelings.filter((f) => (f.touchpoint_ids || []).includes(current?.id)),
    [feelings, current]
  );
  const ranked = useMemo(
    () => [...feelings].sort((a, b) => (b.count || 0) - (a.count || 0)),
    [feelings]
  );
  const linkedIds = new Set(linkedHere.map((f) => f.id));

  if (touchpoints.length === 0) {
    return (
      <div className="space-y-6">
        <PhaseIntro phase={3} title={t("p3_title")} intro={t("p3_intro")} />
        <div className="rounded-2xl border-2 border-dashed border-border bg-card p-10 text-center text-muted-foreground">
          {t("p3_no_touchpoints")}
        </div>
      </div>
    );
  }

  const mention = async ({ text: tx, type, cluster }) => {
    try {
      await api.post("/org/feelings/mention", { text: tx, touchpoint_id: current.id, type, cluster });
      refresh();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const submitNew = (e) => {
    e.preventDefault();
    const tx = text.trim();
    if (!tx) return;
    const existing = feelings.find((f) => f.text.trim().toLowerCase() === tx.toLowerCase());
    if (existing) { mention({ text: tx, type: existing.type }); setText(""); }
    else setPending(tx);
  };

  const confirmType = (type) => {
    mention({ text: pending, type });
    setPending(null); setText("");
  };

  const clickEarlier = (f) => mention({ text: f.text, type: f.type });

  const unlink = async (f) => {
    try { await api.patch(`/org/feelings/${f.id}/unlink`, { touchpoint_id: current.id }); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const runAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/suggest/feelings", { lang, touchpoint: current.text });
      setAi(data.suggestions || []);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setAiBusy(false);
  };

  const move = async (id, cluster) => { await api.patch(`/org/feelings/${id}`, { cluster }); refresh(); };
  const del = async (id) => {
    try { await api.delete(`/org/feelings/${id}`); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const recluster = async () => {
    setReBusy(true);
    try { await api.post("/ai/recluster/feelings", { lang }); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setReBusy(false);
  };

  const accentFor = (f) => {
    const c = f.count || 1;
    const size = c >= 4 ? "text-base font-bold" : c >= 2 ? "text-sm font-semibold" : "text-sm";
    const color = f.type === "desired"
      ? "border-accent/40 bg-accent/10 text-[#9a5a00]"
      : "border-emerald-400/40 bg-emerald-50 text-emerald-800";
    return `${color} ${size}`;
  };
  const renderBadge = (f) => (
    <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">{f.count || 1}×</span>
  );

  return (
    <div className="space-y-6">
      <PhaseIntro phase={3} title={t("p3_title")} intro={t("p3_intro")} />
      <RevisitCallout coreEmotion={org.core_emotion} subject={t("p3_subject")} />

      {/* Walkthrough */}
      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid="p3-walkthrough">
        {/* progress */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <Button data-testid="p3-prev-tp" variant="outline" size="sm" className="rounded-full"
            disabled={idx === 0} onClick={() => { setIdx((i) => Math.max(0, i - 1)); setAi([]); setPending(null); }}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <p className="text-center text-xs font-semibold uppercase tracking-widest text-accent">
              {t("moment_of")} {idx + 1} {t("of_word")} {touchpoints.length}
            </p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${((idx + 1) / touchpoints.length) * 100}%` }} />
            </div>
          </div>
          <Button data-testid="p3-next-tp" variant="outline" size="sm" className="rounded-full"
            disabled={idx >= touchpoints.length - 1} onClick={() => { setIdx((i) => Math.min(touchpoints.length - 1, i + 1)); setAi([]); setPending(null); }}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* current touchpoint */}
        <div className="rounded-xl bg-gradient-to-br from-secondary to-card p-5 text-center">
          <MessageCircle className="mx-auto h-6 w-6 text-primary" />
          <h3 className="mt-2 font-display text-2xl font-bold text-primary" data-testid="p3-current-touchpoint">{current.text}</h3>
          {current.cluster && <span className="mt-1 inline-block rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">{current.cluster}</span>}
        </div>

        {/* linked feelings for this touchpoint */}
        <div className="mt-5">
          <p className="mb-2 text-sm font-semibold text-foreground">{t("linked_here")}</p>
          <div className="flex flex-wrap gap-2 min-h-[2rem]">
            {linkedHere.length === 0 && <span className="text-sm text-muted-foreground">{t("no_feelings_here")}</span>}
            <AnimatePresence>
              {linkedHere.map((f) => (
                <motion.span key={f.id} layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  data-testid={`p3-linked-${f.id}`}
                  className={`group flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold ${f.type === "desired" ? "border-accent/40 bg-accent/10 text-[#9a5a00]" : "border-emerald-400/40 bg-emerald-50 text-emerald-800"}`}>
                  {f.text}
                  <span className="rounded-full bg-primary/10 px-1.5 text-xs font-bold text-primary">{f.count || 1}×</span>
                  {isAdmin && (
                    <button onClick={() => unlink(f)} aria-label="unlink" data-testid={`p3-unlink-${f.id}`}>
                      <X className="h-3.5 w-3.5 text-destructive opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                  )}
                </motion.span>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* add new feeling */}
        <div className="mt-5">
          <p className="mb-2 text-sm font-semibold text-foreground">{t("feeling_prompt")}</p>
          {pending ? (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-secondary p-3" data-testid="p3-type-ask">
              <span className="text-sm font-semibold text-primary">"{pending}" — {t("ask_type")}</span>
              <Button data-testid="p3-type-practice" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => confirmType("practice")}>
                {t("in_practice")}
              </Button>
              <Button data-testid="p3-type-desired" size="sm" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => confirmType("desired")}>
                {t("desired")}
              </Button>
              <button onClick={() => setPending(null)} className="text-sm text-muted-foreground hover:underline">✕</button>
            </div>
          ) : (
            <form onSubmit={submitNew} className="flex gap-2">
              <Input data-testid="p3-input" value={text} onChange={(e) => setText(e.target.value)}
                placeholder={lang === "nl" ? "Bijv. vertrouwen, verrassing, trots…" : "E.g. trust, surprise, pride…"} className="rounded-full" />
              <Button type="submit" data-testid="p3-add" className="shrink-0 rounded-full"><Plus className="mr-1 h-4 w-4" />{t("add")}</Button>
            </form>
          )}
        </div>

        {/* earlier feelings clickable */}
        {ranked.length > 0 && (
          <div className="mt-5">
            <p className="mb-2 text-sm font-semibold text-foreground">{t("earlier_feelings")}</p>
            <div className="flex flex-wrap gap-2">
              {ranked.map((f) => {
                const active = linkedIds.has(f.id);
                return (
                  <button key={f.id} data-testid={`p3-earlier-${f.id}`} onClick={() => clickEarlier(f)}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-secondary"}`}>
                    {active && <Check className="h-3.5 w-3.5" />}
                    {f.text}
                    <span className={`rounded-full px-1.5 text-xs font-bold ${active ? "bg-white/20" : "bg-primary/10 text-primary"}`}>{f.count || 1}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* AI for this touchpoint */}
        <div className="mt-5 space-y-3">
          <AiButton testId="p3-ai" onClick={runAi} remaining={remaining} busy={aiBusy} label={t("ai_suggest")} />
          {ai.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-accent"><Sparkles className="h-4 w-4" />{t("ai_for_touchpoint")}</p>
              <div className="flex flex-wrap gap-2">
                {ai.map((s, i) => (
                  <button key={i} data-testid={`p3-ai-chip-${i}`} onClick={() => mention({ text: s.text, type: s.type, cluster: s.cluster })}
                    className="flex items-center gap-1.5 rounded-full border border-dashed border-accent/50 bg-accent/5 px-3 py-1.5 text-sm font-medium text-accent hover:bg-accent hover:text-accent-foreground">
                    <Plus className="h-3.5 w-3.5" />{s.text}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Weighted overview */}
      {feelings.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-display text-xl font-semibold text-primary">{t("weight_overview")}</h3>
            {feelings.length > 1 && <ReclusterButton testId="p3-recluster" onClick={recluster} busy={reBusy} label={t("ai_recluster")} />}
          </div>
          <div className="flex gap-4 text-xs font-semibold">
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-emerald-500" /> {t("in_practice")}</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-accent" /> {t("desired")}</span>
          </div>
          <DragBoard items={feelings} onMove={move} onDelete={del} canDelete={isAdmin} accentFor={accentFor} renderBadge={renderBadge} />
        </div>
      )}
    </div>
  );
}
